import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import moment from "moment";
import {
  updateFilters,
  fetchBranches,
  updateBranchFilter,
  prefetchAllBranchesData,
  setCurrentBranch,
  setSelectedBranchType,
} from "slices/dashboardFilters";
import { RootState } from "state";
import styles from "./index.module.css";
import arrowDown from "../../assets/images/waiterReports/arrowDown.png";
import DateSelector from "./DateSelector";
import SvgDarkIcon from "components/SvgIcon/LogoDark";

interface Branch {
  objectId: string;
  name: string;
}

interface HeaderProps {
  isMobileMenuOpen?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isMobileMenuOpen = false }) => {
  // Select only needed values to prevent unnecessary re-renders
  const startDate = useSelector(
    (state: RootState) => state.dashboardFilters.startDate
  );
  const endDate = useSelector(
    (state: RootState) => state.dashboardFilters.endDate
  );
  const branches = useSelector(
    (state: RootState) => state.dashboardFilters.branches,
    shallowEqual
  );
  const branchFilter = useSelector(
    (state: RootState) => state.dashboardFilters.branchFilter,
    shallowEqual
  );
  const loading = useSelector(
    (state: RootState) => state.dashboardFilters.loading
  );
  const ageRange = useSelector(
    (state: RootState) => state.dashboardFilters.ageRange
  );
  const genderFilter = useSelector(
    (state: RootState) => state.dashboardFilters.genderFilter
  );
  const ordersFilter = useSelector(
    (state: RootState) => state.dashboardFilters.ordersFilter
  );
  const selectedDateOption = useSelector(
    (state: RootState) => state.dashboardFilters.selectedDateOption
  );
  const currentBranchId = useSelector(
    (state: RootState) => state.dashboardFilters.currentBranchId
  );
  const currentBranchName = useSelector(
    (state: RootState) => state.dashboardFilters.currentBranchName
  );
  const allBranchIds = useSelector(
    (state: RootState) => state.dashboardFilters.allBranchIds,
    shallowEqual
  );

  const dispatch = useDispatch();
  const location = useLocation();

  const [showDateSelector, setShowDateSelector] = useState(false);
  const [showBranches, setShowBranches] = useState(false);
  const [showAgeGroup] = useState(false);
  const [showGender] = useState(false);
  const [showOrdersFilters, setShowOrdersFilters] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState<string[]>(
    branchFilter || []
  );
  const [searchTerm, setSearchTerm] = useState("");

  const [tempStartDate, setTempStartDate] = useState<moment.Moment | null>(
    startDate ? moment(startDate) : null
  );
  const [tempEndDate, setTempEndDate] = useState<moment.Moment | null>(
    endDate ? moment(endDate) : null
  );
  const [tempAgeRange, setTempAgeRange] = useState<string | null>(ageRange);
  const [tempGenderFilter, setTempGenderFilter] = useState<string | null>(
    genderFilter
  );
  const [tempOrdersFilter, setTempOrdersFilter] = useState<string | null>(
    ordersFilter
  );
  const [tempSelectedBranches, setTempSelectedBranches] = useState<string[]>(
    branchFilter || []
  );

  const branchesRef = useRef<HTMLDivElement>(null);
  const dateSelectorRef = useRef<HTMLDivElement>(null);
  const prefetchedRef = useRef(false);

  // Memoize pathname checks to prevent re-renders
  const pathname = useMemo(() => location.pathname, [location.pathname]);
  const isOverviewPage = useMemo(() => pathname === "/overview", [pathname]);
  const isCustomersPage = useMemo(() => pathname === "/customers", [pathname]);
  const isOrdersPage = useMemo(() => pathname === "/orders", [pathname]);
  const isOperationsPage = useMemo(
    () => pathname === "/operations",
    [pathname]
  );

  // Only fetch branches once on mount
  const hasFetchedBranchesRef = useRef(false);


  useEffect(() => {
    if (!hasFetchedBranchesRef.current) {
      hasFetchedBranchesRef.current = true;
      dispatch(fetchBranches());
    }
  }, [dispatch]);

  // Background prefetch when branches and dates are available (only once, with delay)
  useEffect(() => {
    if (
      allBranchIds &&
      allBranchIds.length > 0 &&
      startDate &&
      endDate &&
      (isOverviewPage || isCustomersPage) &&
      !prefetchedRef.current
    ) {
      prefetchedRef.current = true;
      // Delay prefetch significantly to avoid blocking initial load
      // Only prefetch after user has had time to see the initial data
      const prefetchTimeout = setTimeout(() => {
        console.log("🔄 [Header] Starting delayed background prefetch...");
        dispatch(
          prefetchAllBranchesData(
            allBranchIds,
            startDate,
            endDate,
            isCustomersPage
          )
        );
      }, 10000); // Wait 10 seconds after initial load to reduce initial requests

      return () => clearTimeout(prefetchTimeout);
    }
  }, [
    allBranchIds,
    startDate,
    endDate,
    isOverviewPage,
    isCustomersPage,
    dispatch,
  ]);

  useEffect(() => {
    setSelectedBranches(branchFilter || []);
  }, [branchFilter]);

  useEffect(() => {
    setTempSelectedBranches(selectedBranches);
  }, [selectedBranches]);

  // Handle outside clicks for both dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      // Close branches dropdown if clicked outside
      if (
        showBranches &&
        branchesRef.current &&
        !branchesRef.current.contains(target)
      ) {
        setShowBranches(false);
      }

      // Close date selector dropdown if clicked outside
      if (
        showDateSelector &&
        dateSelectorRef.current &&
        !dateSelectorRef.current.contains(target)
      ) {
        console.log("Dropdown closing: Clicked outside", {
          showDateSelector,
          dateRange: { startDate: tempStartDate, endDate: tempEndDate },
        });
        setShowDateSelector(false);
      }
    };

    // Use both mouse and touch events for better mobile support
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showBranches, showDateSelector, tempStartDate, tempEndDate]);

  const toggleState = useCallback(
    (setState: React.Dispatch<React.SetStateAction<boolean>>) => {
      if (!loading) {
        setState((prev) => !prev);
      }
    },
    [loading]
  );

  const handleBranchSelectToggle = useCallback(
    (branchId: string) => {
      if (!loading) {
        setTempSelectedBranches((prev) => {
          if (prev.includes(branchId) && prev.length > 1) {
            return prev.filter((id) => id !== branchId);
          } else if (!prev.includes(branchId)) {
            return [...prev, branchId];
          }
          return prev;
        });
      }
    },
    [loading]
  );

  const handleBranchClick = useCallback(
    (branchId: string | "all", branchName: string) => {
      if (!loading) {
        // For Overview and Customers, single selection only
        if (isOverviewPage || isCustomersPage) {
          if (branchId === "all") {
            // Select all branches - use allBranchIds from getBranches response
            if (allBranchIds && allBranchIds.length > 0) {
              console.log(
                "🔍 [Header] Selecting 'All' branches:",
                allBranchIds
              );
              dispatch(setSelectedBranchType("all"));
              dispatch(setCurrentBranch({ id: "all", name: "All" }));
              // Create a new array to ensure mutability
              const allIds = Array.from(allBranchIds);
              dispatch(updateBranchFilter({ branchFilter: allIds }));
              console.log(
                "✅ [Header] Updated branchFilter to all branches:",
                allIds
              );
            } else {
              console.warn(
                "⚠️ [Header] allBranchIds is empty, cannot select 'All'"
              );
            }
            setShowBranches(false);
          } else {
            // Select single branch - only update if different branch is selected
            if (branchId !== currentBranchId) {
              console.log("🔍 [Header] Selecting single branch:", branchId);
              dispatch(setSelectedBranchType("single"));
              dispatch(setCurrentBranch({ id: branchId, name: branchName }));
              dispatch(updateBranchFilter({ branchFilter: [branchId] }));
              console.log(
                "✅ [Header] Updated branchFilter to single branch:",
                [branchId]
              );
              setShowBranches(false);
            }
          }
        } else {
          // For other pages, maintain multi-select behavior
          if (typeof branchId === "string" && branchId !== "all") {
            handleBranchSelectToggle(branchId);
          }
        }
      }
    },
    [
      loading,
      currentBranchId,
      isOverviewPage,
      isCustomersPage,
      allBranchIds,
      dispatch,
      handleBranchSelectToggle,
    ]
  );

  const handleGenericSelectToggle = useCallback(
    (
      selectedItem: string,
      setSelectedItem: React.Dispatch<React.SetStateAction<string | null>>,
      currentSelection: string | null
    ) => {
      const newSelection =
        selectedItem === currentSelection ? "All" : selectedItem;
      setSelectedItem(newSelection);

      dispatch(
        updateFilters(
          tempStartDate,
          tempEndDate,
          tempAgeRange,
          tempGenderFilter,
          newSelection,
          tempSelectedBranches,
          selectedDateOption
        )
      );
    },
    [
      dispatch,
      tempStartDate,
      tempEndDate,
      tempAgeRange,
      tempGenderFilter,
      tempSelectedBranches,
      selectedDateOption,
    ]
  );

  const handleDateChange = useCallback(
    ({ startDate, endDate, selectedOption }) => {
      console.log("onDateChange fired (parent)", {
        startDate,
        endDate,
        selectedOption,
        hasStart: !!startDate,
        hasEnd: !!endDate,
        loading,
      });
      if (!loading) {
        setTempStartDate(startDate);
        setTempEndDate(endDate);
        // Only send request and close dropdown when both dates are selected
        if (startDate && endDate) {
          console.log(
            "onDateChange: Both dates present - dispatching updateFilters and closing dropdown"
          );
          dispatch(
            updateFilters(
              startDate,
              endDate,
              ageRange,
              genderFilter,
              ordersFilter,
              tempSelectedBranches,
              selectedOption
            )
          );
          // Close dropdown after request is sent
          console.log("onDateChange: Setting setShowDateSelector(false)");
          setShowDateSelector(false);
        } else {
          console.log(
            "onDateChange: Missing dates - NOT dispatching or closing",
            {
              startDate: !!startDate,
              endDate: !!endDate,
            }
          );
        }
      } else {
        console.log("onDateChange: Blocked - loading is true");
      }
    },
    [
      loading,
      dispatch,
      ageRange,
      genderFilter,
      ordersFilter,
      tempSelectedBranches,
    ]
  );

  const handleConfirm = useCallback(() => {
    if (!loading) {
      dispatch(
        updateFilters(
          tempStartDate,
          tempEndDate,
          tempAgeRange,
          tempGenderFilter,
          tempOrdersFilter,
          tempSelectedBranches,
          selectedDateOption
        )
      );
      setShowBranches(false);
    }
  }, [
    loading,
    dispatch,
    tempStartDate,
    tempEndDate,
    tempAgeRange,
    tempGenderFilter,
    tempOrdersFilter,
    tempSelectedBranches,
    selectedDateOption,
  ]);

  const getSelectedBranchNames = useMemo(() => {
    const selectedBranchNames = branches
      .filter((branch: Branch) =>
        tempSelectedBranches.includes(branch.objectId)
      )
      .map((branch: Branch) => branch.name);
    return selectedBranchNames.length <= 2
      ? selectedBranchNames.join(", ")
      : `${selectedBranchNames[0]} (+${selectedBranchNames.length - 2})`;
  }, [branches, tempSelectedBranches]);

  // Format date range for display when Custom is selected
  const getDateRangeDisplay = useMemo(() => {
    if (selectedDateOption === "Custom" && startDate && endDate) {
      const start = moment(startDate);
      const end = moment(endDate);
      return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
    }
    return selectedDateOption;
  }, [selectedDateOption, startDate, endDate]);

  const filteredBranches = useMemo(
    () =>
      branches.filter((branch: Branch) =>
        branch.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [searchTerm, branches]
  );

  const renderDropdown = useCallback(
    (items: string[], onSelect: (item: string) => void) => (
      <div className={styles.dropdown}>
        <ul className={styles.branches}>
          {items.map((item) => (
            <li
              key={item}
              onClick={() => onSelect(item)}
              style={{
                cursor: "pointer",
                color: tempSelectedBranches.includes(item)
                  ? "black"
                  : "#3F425480",
              }}
            >
              {item}
            </li>
          ))}
        </ul>
      </div>
    ),
    [tempSelectedBranches]
  );

  return (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <div className={styles.dateOptionContainer}>
          <h3
            onClick={() => toggleState(setShowDateSelector)}
            style={{
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.5 : 1,
              color: selectedDateOption ? "#3F4254" : "#3F425480",
            }}
          >
            {getDateRangeDisplay} <img src={arrowDown} alt="show more" />
          </h3>
          {showDateSelector && (
            <>
              <div
                className={styles.backdrop}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log("Dropdown closing: Backdrop clicked", {
                    showDateSelector,
                    dateRange: {
                      startDate: tempStartDate,
                      endDate: tempEndDate,
                    },
                  });
                  setShowDateSelector(false);
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  console.log("Dropdown closing: Backdrop touched", {
                    showDateSelector,
                    dateRange: {
                      startDate: tempStartDate,
                      endDate: tempEndDate,
                    },
                  });
                  setShowDateSelector(false);
                }}
              />
              <div ref={dateSelectorRef} className={styles.dateSelectorWrapper}>
                <DateSelector
                  onDateChange={handleDateChange}
                  initialStartDate={tempStartDate}
                  initialEndDate={tempEndDate}
                  initialSelectedOption={selectedDateOption}
                  onClose={() => {
                    console.log("Dropdown closing: onClose callback called", {
                      showDateSelector,
                      dateRange: {
                        startDate: tempStartDate,
                        endDate: tempEndDate,
                      },
                    });
                    setShowDateSelector(false);
                  }}
                />
              </div>
            </>
          )}
        </div>
        {/* For Overview and Customers: Show current branch name with single selection */}
        {(isOverviewPage || isCustomersPage) && currentBranchName && (
          <div
            ref={branchesRef}
            className={styles.branchNameContainer}
            style={{ position: "relative" }}
          >
            <h3
              onClick={() => !loading && toggleState(setShowBranches)}
              style={{
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.5 : 1,
                color: currentBranchName ? "#3F4254" : "#3F425480",
              }}
            >
              {currentBranchName}
              <img
                src={arrowDown}
                alt="show more"
                style={{ opacity: loading ? 0.5 : 1 }}
              />
            </h3>
            {showBranches && (
              <>
                <div
                  className={styles.backdrop}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowBranches(false);
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    setShowBranches(false);
                  }}
                />
                <div className={styles.branchesDropdown}>
                  <button
                    className={styles.closeButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowBranches(false);
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      setShowBranches(false);
                    }}
                    aria-label="Close branches dropdown"
                    type="button"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <div className={styles.searchRow}>
                    <input
                      type="text"
                      placeholder="Search branches..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={styles.branchSearch}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <ul className={styles.branches}>
                    <li
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBranchClick("all", "All");
                      }}
                      className={
                        currentBranchId === "all" ? styles.selected : ""
                      }
                      style={{
                        cursor: "pointer",
                        color:
                          currentBranchId === "all" ? "#f13c4f" : "#3F425480",
                        fontWeight: currentBranchId === "all" ? "600" : "400",
                      }}
                    >
                      All
                    </li>
                    {filteredBranches.map((branch: Branch) => (
                      <li
                        key={branch.objectId}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBranchClick(branch.objectId, branch.name);
                        }}
                        className={
                          branch.objectId === currentBranchId
                            ? styles.selected
                            : ""
                        }
                        style={{
                          cursor: "pointer",
                          color:
                            branch.objectId === currentBranchId
                              ? "#f13c4f"
                              : "#3F425480",
                        }}
                      >
                        {branch.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        )}

        {/* For other pages: Multi-select behavior */}
        {!isOverviewPage &&
          !isCustomersPage &&
          tempSelectedBranches.length > 0 &&
          getSelectedBranchNames.length !== 0 && (
            <div ref={branchesRef} style={{ position: "relative" }}>
              <h3
                onClick={() => !loading && toggleState(setShowBranches)}
                style={{
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                  color: tempSelectedBranches.length ? "black" : "#3F425480",
                }}
              >
                {getSelectedBranchNames}
                <img
                  src={arrowDown}
                  alt="show more"
                  style={{ opacity: loading ? 0.5 : 1 }}
                />
              </h3>
              {showBranches && (
                <>
                  <div
                    className={styles.backdrop}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowBranches(false);
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      setShowBranches(false);
                    }}
                  />
                  <div className={styles.branchesDropdown}>
                    <button
                      className={styles.closeButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowBranches(false);
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        setShowBranches(false);
                      }}
                      aria-label="Close branches dropdown"
                      type="button"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 6L18 18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className={styles.searchRow}>
                      <input
                        type="text"
                        placeholder="Search branches..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.branchSearch}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirm();
                        }}
                        disabled={loading}
                        className={styles.confirmButton}
                      >
                        {loading ? "Loading..." : "Confirm"}
                      </button>
                    </div>
                    <ul className={styles.branches}>
                      {filteredBranches.map((branch: Branch) => (
                        <li
                          key={branch.objectId}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBranchSelectToggle(branch.objectId);
                          }}
                          className={
                            tempSelectedBranches.includes(branch.objectId)
                              ? styles.selected
                              : ""
                          }
                          style={{
                            cursor: "pointer",
                            opacity:
                              tempSelectedBranches.length === 1 &&
                              tempSelectedBranches.includes(branch.objectId)
                                ? 0.5
                                : 1,
                            color: tempSelectedBranches.includes(
                              branch.objectId
                            )
                              ? "black"
                              : "#3F425480",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={tempSelectedBranches.includes(
                              branch.objectId
                            )}
                            readOnly
                            disabled={
                              tempSelectedBranches.length === 1 &&
                              tempSelectedBranches.includes(branch.objectId)
                            }
                          />
                          {branch.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}
        {!isOverviewPage && !isCustomersPage && (
          <h3
            onClick={() => toggleState(setShowOrdersFilters)}
            style={{
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.5 : 1,
              color: tempOrdersFilter ? "black" : "#3F425480",
            }}
          >
            {tempOrdersFilter || "Orders"}
            <img src={arrowDown} alt="show more" />
            {showOrdersFilters &&
              renderDropdown(["Dine-in", "Pickup", "Delivery"], (item) =>
                handleGenericSelectToggle(
                  item,
                  setTempOrdersFilter,
                  tempOrdersFilter
                )
              )}
          </h3>
        )}
        {!isOverviewPage &&
          !isCustomersPage &&
          !isOrdersPage &&
          !isOperationsPage && (
            <>
              <h3
                // onClick={() => toggleState(setShowAgeGroup)}
                onClick={() => null}
                style={{
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                  color: tempAgeRange ? "black" : "#3F425480",
                }}
              >
                {tempAgeRange || "Age"}
                <img src={arrowDown} alt="show more" />
                {showAgeGroup &&
                  renderDropdown(
                    ["All", "8-13", "13-18", "18-26", "31-40"],
                    (item) =>
                      handleGenericSelectToggle(
                        item,
                        setTempAgeRange,
                        tempAgeRange
                      )
                  )}
              </h3>
              <h3
                // onClick={() => toggleState(setShowGender)}
                onClick={() => null}
                style={{
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                  color: tempGenderFilter ? "black" : "#3F425480",
                }}
              >
                {tempGenderFilter || "Gender"}
                <img src={arrowDown} alt="show more" />
                {showGender &&
                  renderDropdown(["All", "Male", "Female"], (item) =>
                    handleGenericSelectToggle(
                      item,
                      setTempGenderFilter,
                      tempGenderFilter
                    )
                  )}
              </h3>
            </>
          )}
      </div>
      <div
        className={`${styles.logoContainer} ${
          isMobileMenuOpen ? styles.logoHidden : ""
        }`}
      >
        <SvgDarkIcon size={80} />
      </div>
    </div>
  );
};

export default Header;
